import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, where, onSnapshot } from 'firebase/firestore';

// Import Tab Components from the components folder
import PayoutsTab from '../components/admin/PayoutsTab';
import ServicesTab from '../components/admin/ServicesTab';
import UsersTab from '../components/admin/UsersTab';
import TracksTab from '../components/admin/TracksTab';
import SalesTab from '../components/admin/SalesTab';
import ChartsTab from '../components/admin/ChartsTab';
import RadioManagerTab from '../components/admin/radioManager/RadioManagerTab';
import CopyrightTab from '../components/admin/CopyrightTab';
import FansTab from '../components/admin/FansTab';
import TomTab from '../components/admin/TomTab'; // 🔑 Import TOM Tab

/**
 * AdminPage Shell
 * Located in pages/ folder.
 * Manages active tab state and master layout for the Admin Dashboard.
 */
const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('fans');
  const [counts, setCounts] = useState({ payouts: 0, services: 0, copyright: 0, tomPending: 0 });
  const db = getFirestore();

  useEffect(() => {
    // 1. Query pending service requests
    const qAllPending = query(
      collection(db, 'serviceRequests'),
      where('status', '==', 'pending')
    );

    const unsubscribeServices = onSnapshot(qAllPending, (snapshot) => {
      let payoutCount = 0;
      let serviceCount = 0;

      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.type === 'payout_request') {
          payoutCount++;
        } else {
          serviceCount++;
        }
      });

      setCounts(prev => ({ ...prev, payouts: payoutCount, services: serviceCount }));
    });

    // 2. Query pending copyright manual reviews
    const qCopyrightPending = query(
      collection(db, 'copyright_reports'),
      where('status', '==', 'manual_review_required')
    );

    const unsubscribeCopyright = onSnapshot(qCopyrightPending, (snapshot) => {
      setCounts(prev => ({ ...prev, copyright: snapshot.size }));
    });

    // 🔑 3. Query TOM pending message queue items
    const qTomPending = query(
      collection(db, 'messageQueue'),
      where('status', 'in', ['pending_review', 'pending_ai', 'pending', 'awaiting_review'])
    );

    const unsubscribeTom = onSnapshot(qTomPending, (snapshot) => {
      setCounts(prev => ({ ...prev, tomPending: snapshot.size }));
    });

    return () => {
      unsubscribeServices();
      unsubscribeCopyright();
      unsubscribeTom();
    };
  }, [db]);

  // Helper function to render the active tab view
  const renderTabContent = () => {
    switch (activeTab) {
      case 'tom':
        return <TomTab />; // 🔑 TOM Tab View
      case 'fans':
        return <FansTab />;
      case 'charts':
        return <ChartsTab />;
      case 'radio':
        return <RadioManagerTab />;
      case 'payouts':
        return <PayoutsTab />;
      case 'services':
        return <ServicesTab />;
      case 'users':
        return <UsersTab />;
      case 'tracks':
        return <TracksTab />;
      case 'sales':
        return <SalesTab />;
      case 'copyright':
        return <CopyrightTab />;
      default:
        return <FansTab />;
    }
  };

  // Tab configuration for navigation bar
  const tabs = [
    { id: 'fans', label: 'Supafan Hub' },
    { id: 'tom', label: 'TOM AI Hub', count: counts.tomPending }, // 🔑 Integrated TOM Tab
    { id: 'charts', label: 'Charts' },
    { id: 'radio', label: 'Radio Manager' },
    { id: 'copyright', label: 'Copyright Queue', count: counts.copyright },
    { id: 'payouts', label: 'Payouts', count: counts.payouts },
    { id: 'services', label: 'Services', count: counts.services },
    { id: 'users', label: 'Users' },
    { id: 'tracks', label: 'Tracks' },
    { id: 'sales', label: 'Sales' },
  ];

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <header className="max-w-7xl mx-auto mb-8">
        <h1 className="text-4xl font-black text-slate-900 tracking-tight">
          Admin <span className="text-blue-600">Dashboard</span>
        </h1>
        <p className="text-slate-500 font-medium">Atomic Operating System & Platform Management</p>
      </header>

      <main className="max-w-7xl mx-auto">
        {/* Persistent Tab Navigation */}
        <nav className="flex bg-white rounded-t-2xl shadow-sm border-b overflow-x-auto no-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative py-5 px-8 text-sm font-bold uppercase tracking-widest transition-all whitespace-nowrap border-b-4 ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 bg-blue-50/30'
                  : 'border-transparent text-gray-400 hover:text-slate-600 hover:bg-gray-50'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-2 inline-flex items-center justify-center bg-red-500 text-white text-[10px] font-black h-5 w-5 rounded-full shadow-sm animate-pulse">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Dynamic Content Area */}
        <div className="bg-white rounded-b-2xl shadow-xl overflow-hidden min-h-[700px]">
          {renderTabContent()}
        </div>
      </main>

      {/* Footer / Status Bar */}
      <footer className="max-w-7xl mx-auto mt-8 flex justify-between items-center text-xs text-gray-400 font-medium">
        <p>© 2026 Atomic Music Platform | AOS v1.0</p>
        <div className="flex gap-4">
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-green-500"></span>
            System Live
          </span>
          <span>Firebase Encrypted</span>
        </div>
      </footer>
    </div>
  );
};

export default AdminPage;